import React, { useState } from 'react';
import Spacer from 'react-spacer';

// @mui/material components
import { makeStyles } from '@mui/styles';
// @mui/icons-material
import ReferenceIcon from '@mui/icons-material/Announcement';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';

// core components
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Table from 'components/Table/Table.js';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/clientEditStyle.js';

import { selectLocale } from '@features/session/sessionSlice';
import { useSelector } from 'react-redux';

import { useGetBaseReferenceListsQuery } from 'api/reference-list.api.js';
import { useAvailableLanguages } from 'hooks/useAvailableLanguages';

const useStyles = makeStyles(styles);

export default function ReferenceLists() {
  const classes = useStyles();
  const { data: referenceLists } = useGetBaseReferenceListsQuery();
  const supportedLanguages = useAvailableLanguages();

  const [defaultBehavior, setDefaultBehavior] = useState('first');
  const [defaultSelection, setDefaultSelection] = useState(0);
  const [currentList, setCurrentList] = useState(null);
  const [expanded, setExpanded] = React.useState(false);

  const handleExpansionChange = (panel, list) => (event, isExpanded) => {
    setCurrentList(list);
    setDefaultBehavior(list.default_type);
    setDefaultSelection(list.default_value);
    setExpanded(isExpanded ? panel : false);
  };

  const getLanguages = () => {
    return supportedLanguages?.join(', ');
  };

  const defaultBehaviorHandler = (event, list) => {
    setCurrentList(list);
    setDefaultBehavior(event.target.value);
    if (event.target.value === 'default')
      if (defaultSelection === '0') setDefaultSelection('1');
    if (event.target.value === 'first') setDefaultSelection('1');
    if (event.target.value === 'none') setDefaultSelection('0');
  };

  const defaultSelectionHandler = (event) => {
    setDefaultSelection(event.target.value.toString());
  };

  // create the data list of entries for a specific reference list for display in a table
  const listEntries = (list) => {
    const getLabel = (labels) => {
      const locale = useSelector(selectLocale);
      const labelLocale = labels.findLast(
        (label) => label?.language === locale
      );
      const firstLabel = labels.find((label) => label?.label?.trim() !== '');
      return labelLocale === undefined || labelLocale?.label?.trim() === ''
        ? firstLabel
        : labelLocale;
    };
    const tableList = list.list.map((entry, key) => {
      let entries = [];
      const result = getLabel(entry.entry_label);
      entries.push(entry.entry_code);
      entries.push(result?.label);
      return entries;
    });
    return tableList;
  };

  // create the list of default list options for selection by the user
  const defaultOptions = (list) => {
    const optionsList = list.list.map((entry, key) => (
      <MenuItem key={list.name + '-default-' + (key + 1)} value={key + 1}>
        {entry.entry_label[0].label}
      </MenuItem>
    ));
    optionsList.splice(
      0,
      0,
      <MenuItem key='default-0' value={0}>
        -none-
      </MenuItem>
    );
    return optionsList;
  };

  const ReferenceLists = () => {
    return [
      referenceLists?.map((entry, key) => {
        const tData = listEntries(entry);
        const defaultOptionsList = defaultOptions(entry);
        return (
          <Accordion
            expanded={expanded === 'panel' + key}
            onChange={handleExpansionChange('panel' + key, entry)}
            key={key}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='panel1a-content'
              id={entry.name}
            >
              <Typography className={classes.heading}>
                {entry.name.toUpperCase()}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <GridContainer>
                <GridItem xs={8}>
                  <Table tableHead={['Code', 'Label']} tableData={tData} />
                </GridItem>
                <GridItem xs={4}>
                  <GridContainer>
                    <GridItem xs={12}>
                      <Spacer height='36px' />
                    </GridItem>
                    <GridItem xs={12}>
                      <FormControl
                        variant='standard'
                        className={classes.formControl}
                      >
                        <InputLabel
                          id='behavior-select-label'
                          className={classes.inputLabel}
                        >
                          Default Behavior
                        </InputLabel>
                        <Select
                          variant='standard'
                          className={classes.select}
                          labelId='behavior-select-label'
                          id={'behavior-select-' + entry.name}
                          value={defaultBehavior}
                          onChange={(event) =>
                            defaultBehaviorHandler(event, entry)
                          }
                        >
                          <MenuItem key='default' value='default'>
                            Use Selected Default
                          </MenuItem>
                          <MenuItem key='first' value='first'>
                            Default to First in List
                          </MenuItem>
                          <MenuItem key='none' value='none'>
                            No Default - Require Selection
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem xs={12}>
                      <Spacer height='24px' />
                    </GridItem>
                    <GridItem xs={12}>
                      <FormControl
                        variant='standard'
                        className={classes.formControl}
                      >
                        <InputLabel
                          id='default-select-label'
                          className={classes.inputLabel}
                        >
                          Default Selection
                        </InputLabel>
                        <Select
                          variant='standard'
                          className={classes.select}
                          labelId='default-select-label'
                          id={'default-select-' + entry.name}
                          value={defaultSelection}
                          onChange={(event) =>
                            defaultSelectionHandler(event, entry)
                          }
                        >
                          {defaultOptionsList}
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem xs={12}>
                      <Spacer height='24px' />
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            </AccordionDetails>
          </Accordion>
        );
      }),
    ];
  };

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color='primary' icon>
            <CardIcon color='primary'>
              <ReferenceIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Default Reference Lists</h4>
          </CardHeader>
          <CardBody>
            <Spacer height='24px' />
            <h5>
              Supported languages:{' '}
              {supportedLanguages?.length > 0 ? getLanguages() : ''}
            </h5>
            <ReferenceLists />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
